import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import "./Product_details_swiper.css";

import { Swiper, SwiperSlide } from "swiper/react";

import { Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";

import Best_seller_heart from "../../common-components/product_card/Best_seller_heart/Best_seller_heart";

import { variant_type } from "../../../utils/common";

function Product_details_swiper({
  product,
  selectedVariant,
}) {
  /* =========================================================
     STATES
  ========================================================= */

  const [activeImage, setActiveImage] = useState("");

  const [zoomPosition, setZoomPosition] = useState({
    x: 50,
    y: 50,
  });

  const [isZoomed, setIsZoomed] = useState(false);

  /* =========================================================
     REFS
  ========================================================= */

  const mainSwiperRef = useRef(null);

  /* =========================================================
     GET VARIANT IMAGES
  ========================================================= */

  const variantImages = useMemo(() => {
    /*
      SINGLE VARIANT
    */
    if (
      Number(product?.variant_type_id) ===
      Number(variant_type.Single)
    ) {
      return selectedVariant?.Product_Images || [];
    }

    /*
      DOUBLE VARIANT
    */
    if (
      Number(product?.variant_type_id) ===
      Number(variant_type.Double)
    ) {
      return (
        selectedVariant?.Product_Variant
          ?.Product_Images || []
      );
    }

    /*
      TRIPLE VARIANT
    */
    if (
      Number(product?.variant_type_id) ===
      Number(variant_type.Triple)
    ) {
      return (
        selectedVariant?.Double_Variant
          ?.Product_Variant
          ?.Product_Images || []
      );
    }

    return [];
  }, [
    product?.variant_type_id,
    selectedVariant,
  ]);

  /* =========================================================
     CREATE FINAL IMAGE ARRAY
  ========================================================= */

  const images = useMemo(() => {
    const allImages = [];

    /*
      MAIN PRODUCT IMAGE
    */

    if (product?.image) {
      allImages.push({
        id: `product-main-${product?.id}`,
        image: product.image,
        isMainProductImage: true,
      });
    }

    /*
      VARIANT IMAGES
    */

    if (variantImages?.length > 0) {
      variantImages.forEach((item, index) => {
        if (
          item?.image &&
          item?.image !== product?.image
        ) {
          allImages.push({
            ...item,
            id:
              item?.id ||
              `variant-image-${index}-${item?.image}`,
          });
        }
      });
    }

    return allImages;
  }, [
    product?.image,
    product?.id,
    variantImages,
  ]);

  /* =========================================================
     RESET WHEN IMAGES CHANGE
  ========================================================= */

  useEffect(() => {
    if (images?.length > 0) {
      setActiveImage(images[0]?.image);
    } else {
      setActiveImage("");
    }

    resetZoom();
  }, [images]);

  /* =========================================================
     RESET ZOOM
  ========================================================= */

  const resetZoom = () => {
    setIsZoomed(false);

    setZoomPosition({
      x: 50,
      y: 50,
    });
  };

  /* =========================================================
     MOUSE ENTER
  ========================================================= */

  const handleMouseEnter = () => {
    setIsZoomed(true);
  };

  /* =========================================================
     MOUSE MOVE
  ========================================================= */

  const handleMouseMove = (event) => {
    if (!isZoomed) {
      return;
    }

    const container =
      event.currentTarget;

    const rect =
      container.getBoundingClientRect();

    /*
      Calculate mouse position
      from 0% to 100%
    */

    let x =
      ((event.clientX - rect.left) /
        rect.width) *
      100;

    let y =
      ((event.clientY - rect.top) /
        rect.height) *
      100;

    /*
      Prevent values outside image
    */

    x = Math.max(0, Math.min(100, x));
    y = Math.max(0, Math.min(100, y));

    setZoomPosition({
      x,
      y,
    });
  };

  /* =========================================================
     MOUSE LEAVE
  ========================================================= */

  const handleMouseLeave = () => {
    resetZoom();
  };

  /* =========================================================
     THUMBNAIL CLICK
  ========================================================= */

  const handleThumbnailClick = (
    image,
    index
  ) => {
    resetZoom();

    setActiveImage(image);

    if (mainSwiperRef.current) {
      mainSwiperRef.current.slideTo(index);
    }
  };

  /* =========================================================
     MAIN SWIPER SLIDE CHANGE
  ========================================================= */

  const handleMainSlideChange = (
    swiper
  ) => {
    const currentImage =
      images?.[swiper.activeIndex]?.image ||
      "";

    setActiveImage(currentImage);

    resetZoom();
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <section className="Product_details_swiper">

      {/* =====================================================
          MAIN IMAGE SECTION
      ===================================================== */}

      <div className="main_img_holder">

        <div className="img_holder">

          <Swiper
            key={
              selectedVariant?.id ||
              product?.id ||
              "default"
            }
            modules={[Navigation]}
            navigation={true}
            slidesPerView={1}
            spaceBetween={10}
            initialSlide={0}
            onSwiper={(swiper) => {
              mainSwiperRef.current =
                swiper;

              if (
                images?.length > 0
              ) {
                setActiveImage(
                  images[0]?.image
                );
              }
            }}
            onSlideChange={
              handleMainSlideChange
            }
            className="mainProductSwiper"
          >

            {images?.map(
              (img, index) => (
                <SwiperSlide
                  key={
                    img?.id ||
                    `${img?.image}-${index}`
                  }
                >

                  {/* =================================================
                      ZOOM CONTAINER
                  ================================================= */}

                  <div
                    className={`zoomable ${
                      isZoomed
                        ? "zoom-active"
                        : ""
                    }`}
                    onMouseEnter={
                      handleMouseEnter
                    }
                    onMouseMove={
                      handleMouseMove
                    }
                    onMouseLeave={
                      handleMouseLeave
                    }
                    style={{
                      "--zoom-pos-x": `${zoomPosition.x}%`,
                      "--zoom-pos-y": `${zoomPosition.y}%`,
                    }}
                  >

                    <img
                      src={
                        process.env
                          .REACT_APP_IMG_URL +
                        img?.image
                      }
                      className="Product_img zoomable__img"
                      alt={
                        img?.isMainProductImage
                          ? product?.name
                          : "Product"
                      }
                      draggable="false"
                    />

                  </div>

                </SwiperSlide>
              )
            )}

          </Swiper>

        </div>

        {/* =====================================================
            WISHLIST
        ===================================================== */}

        <div className="wishlist_div">

          <Best_seller_heart
            product={product}
          />

        </div>

      </div>

      {/* =======================================================
          THUMBNAIL SECTION
      ======================================================= */}

      <div className="swiper_holder">

        <Swiper
          slidesPerView={5}
          spaceBetween={10}
          modules={[Navigation]}
          navigation={true}
          grabCursor={true}
          watchOverflow={true}
          breakpoints={{
            0: {
              slidesPerView: 3,
              spaceBetween: 8,
            },

            576: {
              slidesPerView: 4,
              spaceBetween: 10,
            },

            768: {
              slidesPerView: 5,
              spaceBetween: 10,
            },
          }}
          className="Product_details_Swiper_holder "
        >

          {images?.map(
            (val, index) => (
              <SwiperSlide
                key={
                  val?.id ||
                  `${val?.image}-${index}`
                }
                onClick={() =>
                  handleThumbnailClick(
                    val?.image,
                    index
                  )
                }
              >

                <div
                  className={`img_holder ${
                    activeImage ===
                    val?.image
                      ? "active_thumb"
                      : ""
                  }`}
                >

                  <img
                    src={
                      process.env
                        .REACT_APP_IMG_URL +
                      val?.image
                    }
                    alt={
                      val?.isMainProductImage
                        ? product?.name
                        : "Product Thumbnail"
                    }
                    className="Product_img"
                    draggable="false"
                  />

                </div>

              </SwiperSlide>
            )
          )}

        </Swiper>

      </div>

    </section>
  );
}

export default Product_details_swiper;